import React, { useState, useRef } from 'react';
import { convertToProxyUrl } from '../../utils/imageUrl';

/**
 * RecordStoryCard - 成长记录"故事卡"组件
 * 详细设计理念请参考: docs/UI设计/RecordStoryCard.md
 */
const RecordStoryCard = ({
  artworkPhotos = [],
  classroomPhotos = [],
  remarks = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [failedImages, setFailedImages] = useState(new Set());

  // 触摸滑动
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);
  const [swiping, setSwiping] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);

  // 合并图片
  const allImages = [
    ...artworkPhotos.map((url, i) => ({
      url: convertToProxyUrl(url),
      alt: `作品 ${i + 1}`,
    })),
    ...classroomPhotos.map((url, i) => ({
      url: convertToProxyUrl(url),
      alt: `课堂 ${i + 1}`,
    })),
  ].filter(img => !failedImages.has(img.url));

  const total = allImages.length;

  // ─── 手势处理 ───
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
    setSwiping(true);
  };

  const onTouchMove = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.touches[0].clientX - touchStartX.current;
    touchDeltaX.current = delta;
    const atEdge = (currentIndex === 0 && delta > 0) || (currentIndex === total - 1 && delta < 0);
    setSwipeOffset(atEdge ? delta * 0.3 : delta);
  };

  const onTouchEnd = () => {
    setSwiping(false);
    const threshold = 60;
    if (touchDeltaX.current < -threshold && currentIndex < total - 1) {
      setCurrentIndex(prev => prev + 1);
    } else if (touchDeltaX.current > threshold && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
    setSwipeOffset(0);
    touchStartX.current = null;
  };

  if (total === 0 && !remarks) return null;

  return (
    <div className="relative">
      {/* ─── 照片区 ─── */}
      {total > 0 && (
        <div
          className="relative overflow-hidden bg-gray-50 mt-4"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* 照片主体 */}
          <div
            className="flex"
            style={{
              transform: `translateX(calc(-${currentIndex * 100}% + ${swipeOffset}px))`,
              transition: swiping ? 'none' : 'transform 350ms cubic-bezier(.4,0,.2,1)',
            }}
          >
            {allImages.map((img, i) => (
              <div
                key={i}
                className="w-full flex-shrink-0 flex items-center justify-center bg-gray-50"
                style={{ minHeight: '280px' }}
              >
                <img
                  src={img.url}
                  alt={img.alt}
                  className="w-full max-h-[70vh] object-contain select-none"
                  draggable={false}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  onError={() => setFailedImages(prev => new Set([...prev, img.url]))}
                />
              </div>
            ))}
          </div>

          {/* 底部圆点指示器 */}
          {total > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
              {allImages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  aria-label={`查看第 ${i + 1} 张照片`}
                  className={`rounded-full transition-all duration-300 ${
                    i === currentIndex
                      ? 'w-5 h-2 bg-gray-800/60 shadow-sm'
                      : 'w-2 h-2 bg-gray-400/40'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── 教师点评区：左头像 + 右点评 ─── */}
      {remarks && (
        <div className={`px-5 pb-6 bg-white ${total > 0 ? 'pt-5' : 'pt-5'}`}>
          <div className="flex items-start gap-4">
            {/* 左：教师头像 */}
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-500 flex-shrink-0 flex items-center justify-center text-white text-[11px] font-black shadow-sm ring-2 ring-white mt-0.5">
              夏天
            </div>
            {/* 右：点评内容 */}
            <p className="flex-1 text-sm text-gray-800 leading-relaxed text-justify font-medium min-w-0">
              {remarks}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordStoryCard;
